import { Component, type ErrorInfo, type ReactNode } from 'react'
import { MaintenanceScreen } from 'shared/ui/MaintenanceScreen'

type GlobalErrorBoundaryProps = {
  children: ReactNode
}

type GlobalErrorBoundaryState = {
  hasError: boolean
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === 'string') return new Error(value)
  return new Error('Unexpected application error')
}

export class GlobalErrorBoundary extends Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  state: GlobalErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidMount() {
    window.addEventListener('error', this.handleGlobalError)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError)
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection)
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('GlobalErrorBoundary caught an error', error, info)
  }

  private handleGlobalError = (event: ErrorEvent) => {
    this.setState({ hasError: true })
    console.error('Unhandled window error', toError(event.error ?? event.message))
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    this.setState({ hasError: true })
    console.error('Unhandled promise rejection', toError(event.reason))
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return <MaintenanceScreen onRetry={this.handleReload} />
    }

    return this.props.children
  }
}
